import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  BadgeCheck, MessageCircle, Share2, Wifi, MapPin, ArrowLeft, Trash2, Loader2, Handshake, Edit3,
} from "lucide-react";

export default function CollabView() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/collabs/${id}`).then(r => setC(r.data)).catch(e => setErr(formatErr(e)));
  }, [id]);

  const share = async () => {
    const url = `${window.location.origin}/collabs/${id}`;
    try {
      if (navigator.share) { await navigator.share({ title: c?.title || "Collab", url }); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch { /* cancelled */ }
  };

  const remove = async () => {
    if (!confirm("Delete this collab post?")) return;
    try { await api.delete(`/collabs/${id}`); toast.success("Deleted"); nav("/collabs"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (err) return (
    <div className="max-w-lg mx-auto p-16 text-center">
      <p className="text-[#B84B4B] font-medium">{err}</p>
      <Link to="/collabs" className="btn-ghost inline-flex mt-4"><ArrowLeft size={14}/> Back to collabs</Link>
    </div>
  );
  if (!c) return <div className="p-16 text-center text-[#8A8A8A]"><Loader2 size={16} className="animate-spin inline"/> Loading…</div>;

  const owner = c.owner || {};
  const isOwn = user && user.id === owner.id;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="collab-view">
      <Link to="/collabs" className="text-xs text-[#8A8A8A] hover:text-[#0A0A0A] inline-flex items-center gap-1 mb-4" data-testid="collab-back">
        <ArrowLeft size={12}/> All collabs
      </Link>

      {c.image && (
        <div className="rounded-3xl overflow-hidden bg-[#F2F2F0] aspect-[3/1] mb-6" data-testid="collab-cover">
          <img src={c.image} alt="" data-lightbox className="w-full h-full object-cover"/>
        </div>
      )}

      <p className="overline mb-2 inline-flex items-center gap-1.5"><Handshake size={11}/> Collaboration · {c.category}</p>
      <h1 className="text-3xl sm:text-4xl font-heading font-bold leading-tight" data-testid="collab-title">{c.title}</h1>

      <div className="flex items-center gap-2 flex-wrap text-xs text-[#4A4A4A] mt-3">
        <span className="inline-flex items-center gap-1">
          {c.work_type === "remote" ? <><Wifi size={12}/> Remote</>
            : c.work_type === "hybrid" ? "Hybrid"
            : <><MapPin size={12}/> In-person</>}
        </span>
        {c.location && <span>· {c.location}</span>}
        <span>· {new Date(c.created_at).toLocaleDateString()}</span>
      </div>

      <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-[#0A0A0A] mt-6" data-testid="collab-description">{c.description}</p>

      {/* Real map — shown for in-person / hybrid collabs that have coordinates */}
      {c.work_type !== "remote" && c.lat != null && c.lng != null && (
        <div className="mt-6" data-testid="collab-map-block">
          <p className="text-xs font-medium text-[#4A4A4A] inline-flex items-center gap-1 mb-2">
            <MapPin size={12} className="text-[#0057FF]"/> Where it happens
          </p>
          <div className="rounded-2xl overflow-hidden border border-[#E5E5E3]" data-testid="collab-map">
            <iframe
              title="Collab location"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${c.lng-0.01}%2C${c.lat-0.01}%2C${c.lng+0.01}%2C${c.lat+0.01}&layer=mapnik&marker=${c.lat}%2C${c.lng}`}
              width="100%" height="260"
              style={{ border: 0 }} loading="lazy"
            />
          </div>
          {c.location && (
            <a href={`https://www.openstreetmap.org/?mlat=${c.lat}&mlon=${c.lng}#map=15/${c.lat}/${c.lng}`}
               target="_blank" rel="noopener noreferrer"
               className="text-xs text-[#0057FF] hover:underline mt-1.5 inline-block" data-testid="collab-map-open">
              Open in OpenStreetMap · {c.location}
            </a>
          )}
        </div>
      )}

      {/* Owner card */}
      <div className="card p-5 mt-8 flex items-center gap-4" data-testid="collab-owner-card">
        {owner.avatar && <img src={owner.avatar} alt="" className="h-14 w-14 rounded-full object-cover bg-[#E5E5E3]"/>}
        <div className="flex-1 min-w-0">
          <p className="font-medium inline-flex items-center gap-1.5">
            <Link to={`/u/${owner.id}`} className="hover:underline">{owner.name}</Link>
            {owner.verified && <BadgeCheck size={13} className="text-[#0057FF]"/>}
          </p>
          <p className="text-xs text-[#8A8A8A]">{owner.category}{owner.location && ` · ${owner.location}`}</p>
        </div>
        <Link to={`/u/${owner.id}`} className="btn-ghost text-xs" data-testid="collab-view-profile">View profile</Link>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="collab-actions">
        {!isOwn && user && (
          <Link to={`/messages/${owner.id}?about=collab&id=${c.id}`}
                className="btn-primary" data-testid="collab-message-btn">
            <MessageCircle size={14}/> Message to collaborate
          </Link>
        )}
        {!user && (
          <Link to="/login" className="btn-primary" data-testid="collab-signin-btn">Sign in to collaborate</Link>
        )}
        <button onClick={share} className="btn-secondary" data-testid="collab-share-btn">
          <Share2 size={14}/> Share
        </button>
        {isOwn && (
          <Link to={`/collabs/new?edit=${c.id}`} className="btn-ghost text-sm" data-testid="collab-edit-btn">
            <Edit3 size={13}/> Edit
          </Link>
        )}
        {isOwn && (
          <button onClick={remove} className="btn-ghost text-[#B84B4B] text-sm" data-testid="collab-delete-btn">
            <Trash2 size={12}/> Delete
          </button>
        )}
      </div>
    </div>
  );
}
